import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TenantSummary } from '@vce/shared';
import { api } from '@/lib/api';

const STORAGE_KEY = 'vce.tenant';

interface TenantContextValue {
  tenants: TenantSummary[];
  tenant: TenantSummary | null;
  tenantId: string | null;
  isLoading: boolean;
  select(tenantId: string): void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: api.tenants.list,
  });

  const [preferredId, setPreferredId] = useState<string | null>(() =>
    window.localStorage.getItem(STORAGE_KEY),
  );

  // Um tenant guardado que não existe mais (ou ao qual o usuário perdeu acesso)
  // travaria o painel numa tela de erro, então a escolha é resolvida na
  // renderização: o valor persistido é só uma preferência, não a verdade.
  const tenant = tenants.find((item) => item.id === preferredId) ?? tenants[0] ?? null;
  // Enquanto a lista carrega, o id persistido evita tratar "ainda não chegou" como "sem conta".
  const tenantId = tenant?.id ?? (isLoading ? preferredId : null);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenants,
      tenant,
      tenantId,
      isLoading,
      select: (next: string) => {
        setPreferredId(next);
        window.localStorage.setItem(STORAGE_KEY, next);
      },
    }),
    [tenants, tenant, tenantId, isLoading],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant precisa estar dentro de TenantProvider');
  return context;
}

/** Lista ainda não chegou — não tratar como "sem conta". */
export function useTenantReady(): TenantContextValue & { isResolving: boolean } {
  const ctx = useTenant();
  return { ...ctx, isResolving: ctx.isLoading && ctx.tenantId === null };
}

/** Para telas que só existem com tenant selecionado. */
export function useRequiredTenantId(): string {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error('Nenhum tenant selecionado');
  return tenantId;
}
